import React from "react";
import { SectionHeader } from "@/shell/components/SectionHeader";
import { SenderProfileResponse } from "@/features/orders-analytics/types/orders-analytics.types";
import { formatters } from "@/features/orders-analytics/utils/formatters";

type Props = {
    data: SenderProfileResponse;
};

const tiles = (data: SenderProfileResponse): { label: string; value: string; sub: string }[] => [
    {
        label: "Active senders",
        value: formatters.formatNumber(data.activeSenders),
        sub: "accounts that created an order",
    },
    {
        label: "KYC coverage",
        value: formatters.formatPercentage(data.kycCoverage),
        sub: "orders with a KYC id",
    },
    {
        label: "Self-created",
        value: formatters.formatPercentage(data.selfCreatedShare),
        sub: "created by the sender in-app",
    },
    {
        label: "Workspace-created",
        value: formatters.formatPercentage(data.workspaceCreatedShare),
        sub: "created by a workspace operator",
    },
];

const maxSenders = (data: SenderProfileResponse): number =>
    data.topCountries.reduce((max, row) => (row.senders > max ? row.senders : max), 0);

export const SenderProfileSection: React.FC<Props> = ({ data }) => {
    const top = maxSenders(data);

    return (
        <section className="section">
            <SectionHeader
                title="Sender profile"
                meta="who initiated the orders — from the order origin (userData)"
            />

            <div className="grid-4">
                {tiles(data).map((tile) => (
                    <div key={tile.label} className="card tile">
                        <div className="tile__label">{tile.label}</div>
                        <div className="tile__value num">{tile.value}</div>
                        <div className="tile__sub">{tile.sub}</div>
                    </div>
                ))}
            </div>

            <div className="card ci-table-card" style={{ marginTop: 12 }}>
                <div className="ci-scroll">
                    <table className="ci-tbl">
                        <thead>
                            <tr>
                                <th scope="col">Sender country</th>
                                <th scope="col" className="num">Senders</th>
                                <th scope="col" className="num">Orders</th>
                                <th scope="col">Share of top</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.topCountries.map((row) => (
                                <tr key={row.country}>
                                    <td style={{ fontWeight: 600 }}>{row.country}</td>
                                    <td className="num">{formatters.formatNumber(row.senders)}</td>
                                    <td className="num">{formatters.formatNumber(row.orders)}</td>
                                    <td>
                                        <div className="ci-bar">
                                            <div className="ci-bar__track">
                                                <div
                                                    className="ci-bar__fill"
                                                    style={{
                                                        width: `${top > 0 ? (row.senders / top) * 100 : 0}%`,
                                                    }}
                                                />
                                            </div>
                                            <span className="ci-bar__value">
                                                {formatters.formatNumber(row.senders)}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
};
